import { useEffect, useMemo, useState } from 'react';
import { apiClient } from '@/lib/api';
import { ENDPOINTS } from '@/lib/api/endpoints';
import { useMasterDataStore } from '@/features/master-data/store/master-data-store';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Loader2, ArrowRight, AlertCircle } from 'lucide-react';
import { formatNumber } from '@/lib/utils/format';

interface RateLookup {
  rate: number;
  effectiveDate: string;
  source: string;
  stale: boolean;
  ageDays: number;
}

interface CurrencySimulatorProps {
  /** Código ISO de la moneda en la que está guardada la cotización (ej. "BOB"). */
  baseCurrencyCode: string;
  /** Símbolo de la moneda base (para mostrar el valor original). */
  baseCurrencySymbol: string;
  /** Fecha de referencia para el TC; por defecto hoy. Usar createdAt de la quote. */
  referenceDate?: string;
  /** Montos principales en moneda base que se van a simular. */
  amounts: Array<{ label: string; value: number | null | undefined; emphasis?: boolean }>;
}

function toFiniteNumber(value: number | null | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0;
  return value;
}

export function CurrencySimulator({
  baseCurrencyCode: rawBaseCurrencyCode,
  baseCurrencySymbol,
  referenceDate,
  amounts,
}: CurrencySimulatorProps) {
  const baseCurrencyCode = (rawBaseCurrencyCode || 'USD').toUpperCase();
  const { currencies, fetchCurrencies } = useMasterDataStore();
  const [displayCode, setDisplayCode] = useState<string>(baseCurrencyCode);
  const [rate, setRate] = useState<RateLookup | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (currencies.length === 0) {
      fetchCurrencies().catch(() => {});
    }
  }, [currencies.length, fetchCurrencies]);

  const currencyOptions = useMemo(
    () =>
      currencies
        .filter((c) => c.isActive)
        .map((c) => ({ code: c.code.toUpperCase(), symbol: c.symbol, name: c.name })),
    [currencies]
  );

  const displaySymbol = useMemo(() => {
    if (displayCode === baseCurrencyCode) return baseCurrencySymbol;
    return currencyOptions.find((c) => c.code === displayCode)?.symbol || displayCode;
  }, [displayCode, baseCurrencyCode, baseCurrencySymbol, currencyOptions]);

  useEffect(() => {
    setError(null);
    if (displayCode === baseCurrencyCode) {
      setRate(null);
      return;
    }
    let cancelled = false;
    const fetchRate = async () => {
      setIsLoading(true);
      try {
        const params = new URLSearchParams({
          from: baseCurrencyCode,
          to: displayCode,
        });
        if (referenceDate) params.set('date', referenceDate);
        const response = await apiClient.get<RateLookup>(
          `${ENDPOINTS.EXCHANGE_RATE_LOOKUP}?${params.toString()}`
        );
        if (!cancelled) {
          setRate(response);
        }
      } catch (err: any) {
        if (!cancelled) {
          setRate(null);
          const msg =
            err?.response?.data?.message ||
            err?.message ||
            `No hay TC ${baseCurrencyCode}→${displayCode} registrado para esta fecha.`;
          setError(msg);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchRate();
    return () => {
      cancelled = true;
    };
  }, [displayCode, baseCurrencyCode, referenceDate]);

  const multiplier = rate?.rate ?? 1;
  const isSimulating = displayCode !== baseCurrencyCode;

  return (
    <div className="rounded-md border bg-muted/30 p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Simular en</span>
          <Select value={displayCode} onValueChange={setDisplayCode}>
            <SelectTrigger className="h-8 w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {currencyOptions.map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  {c.symbol} {c.code} — {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isSimulating && (
            <Badge variant="outline" className="ml-1 text-[10px]">
              Simulación
            </Badge>
          )}
        </div>
        {isSimulating && rate && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>
              TC {baseCurrencyCode} <ArrowRight className="inline w-3 h-3" /> {displayCode}:
            </span>
            <span className="font-mono font-semibold">
              {rate.rate.toLocaleString('es-BO', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 6,
              })}
            </span>
            <span>· {rate.effectiveDate}</span>
            {rate.stale && (
              <Badge variant="outline" className="text-[10px] text-alerta border-alerta-borde">
                {rate.ageDays}d de antigüedad
              </Badge>
            )}
          </div>
        )}
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="w-3 h-3 animate-spin" /> Consultando TC...
        </div>
      )}

      {isSimulating && error && (
        <div className="flex items-center gap-2 text-xs text-destructive">
          <AlertCircle className="w-3 h-3" />
          <span>{error}</span>
          <a
            href="/master-data"
            className="underline underline-offset-2 font-medium hover:text-destructive/80"
          >
            Cargar TC
          </a>
        </div>
      )}

      {isSimulating && rate && (
        <div className="grid grid-cols-1 gap-2 pt-2 border-t">
          {amounts.map((a) => {
            const base = toFiniteNumber(a.value);
            return (
              <div key={a.label} className="flex justify-between items-baseline">
                <span className={`text-muted-foreground ${a.emphasis ? 'font-medium' : 'text-sm'}`}>
                  {a.label}
                </span>
                <div className="flex items-baseline gap-3">
                  <span className="text-xs text-muted-foreground font-mono">
                    {baseCurrencySymbol} {formatNumber(base)}
                  </span>
                  <ArrowRight className="w-3 h-3 text-muted-foreground" />
                  <span
                    className={`font-mono ${a.emphasis ? 'text-xl font-bold text-primary' : 'font-semibold'}`}
                  >
                    {displaySymbol} {formatNumber(base * multiplier)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default CurrencySimulator;
